import { useCallback, useContext, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { FileX, Files } from 'lucide-react';
import type { ObjectData, SceneStore } from '@os/doc';
import { Icon } from '../../ui/icons';
import { ZOOM_EVENT } from '../editor/controller';
import type { MediaResolver } from '../editor/lod';
import { pageLabel } from '../pages/labels';
import { MINIATURE_MIN_PX, MiniatureDepth, PAGE_LINK_HEADER } from '../pages/miniature';
import { PageMiniature } from '../pages/PageMiniature';
import { PageThumb } from '../pages/PageThumb';
import { worldPerScreenPx } from './zoom';

type PageLink = Extract<ObjectData, { type: 'pagelink' }>;

/** Whether `el` (a box `w` world px wide) is at least MINIATURE_MIN_PX wide on screen, updated when the zoom changes. */
function useLargeOnScreen(ref: React.RefObject<HTMLElement | null>, w: number): boolean {
  const [large, setLarge] = useState(true);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // The world layer holds the zoom (`--inv-zoom`, set by the controller): read it there, not through inheritance.
    const world = el.closest('[data-layer="world"]');
    const check = () => setLarge(w / worldPerScreenPx(world ?? el) >= MINIATURE_MIN_PX);
    check();
    world?.addEventListener(ZOOM_EVENT, check);
    return () => world?.removeEventListener(ZOOM_EVENT, check);
  }, [ref, w]);
  return large;
}

/** Whether `el` is in view (always true without IntersectionObserver); a culled link draws nothing either. */
function useInView(ref: React.RefObject<HTMLElement | null>): boolean {
  const [shown, setShown] = useState(true);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => setShown(!!e?.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return shown;
}

/**
 * A page link (P14 Ruling 12): the target page's name over a live miniature of it when visible and large enough on
 * screen and not itself inside a miniature; a static vignette otherwise; « Page supprimée » once the page is gone.
 * Opening (double-click, Enter, « Ouvrir ») belongs to the controller and the context bar.
 */
export function PageLinkObject({ o, store, media }: { o: PageLink; store: SceneStore; media: MediaResolver }) {
  const { t } = useTranslation();
  const depth = useContext(MiniatureDepth);
  const pages = useSyncExternalStore(useCallback((cb: () => void) => store.subscribePages(cb), [store]), () => store.pages());
  const ref = useRef<HTMLDivElement>(null);
  const large = useLargeOnScreen(ref, o.w);
  const inView = useInView(ref);
  const index = pages.findIndex((p) => p.id === o.targetPageId);
  const target = index >= 0 ? pages[index]! : null;
  const bodyH = Math.max(1, o.h - PAGE_LINK_HEADER);
  return (
    <div
      ref={ref}
      data-page-link={o.targetPageId}
      data-page-link-state={!target ? 'deleted' : depth === 0 && large && inView ? 'live' : 'static'}
      className="flex h-full w-full flex-col overflow-hidden rounded-xl border border-border bg-surface font-sans text-fg shadow-sm"
    >
      <div className="flex shrink-0 items-center gap-1.5 border-b border-border px-3 text-sm font-medium" style={{ height: PAGE_LINK_HEADER }}>
        <Icon icon={target ? Files : FileX} size={14} className="shrink-0 text-fg-muted" />
        <span className="truncate">{target ? pageLabel(t, target, index) : t('pages.linkDeleted')}</span>
      </div>
      <div className="relative min-h-0 flex-1 bg-surface-2">
        {!target ? (
          <p className="p-3 text-xs text-fg-muted">{t('pages.linkDeletedHint')}</p>
        ) : depth === 0 && large && inView ? (
          <PageMiniature store={store} pageId={target.id} media={media} w={o.w} h={bodyH} depth={depth} />
        ) : (
          <PageThumb store={store} pageId={target.id} className="absolute inset-0 h-full w-full" />
        )}
      </div>
    </div>
  );
}
